<template>
  <div class="p-4 space-y-4 leave-admin-view">
    <LeaveAdminSectionNav />

    <div class="summary-grid">
      <el-card v-for="card in summaryCards" :key="card.key" shadow="never" class="summary-card">
        <div class="summary-label">{{ card.label }}</div>
        <div class="summary-value">{{ card.value }}</div>
      </el-card>
    </div>

    <el-card shadow="hover" class="rounded-2xl border border-gray-200 quota-card">
      <div class="flex flex-wrap items-center gap-4 mb-4">
        <el-input v-model="filters.employee_name" placeholder="직원명" clearable style="width: 180px" />
        <el-select v-model="filters.company_name" placeholder="회사명" clearable style="width: 180px">
          <el-option v-for="company in companyOptions" :key="company" :label="company" :value="company" />
        </el-select>
        <el-select v-model="filters.department_name" placeholder="부서명" clearable style="width: 180px">
          <el-option v-for="dept in departmentOptions" :key="dept" :label="dept" :value="dept" />
        </el-select>
        <el-input v-model="filters.year" placeholder="연도" clearable style="width: 120px" type="number" />
        <el-button type="primary" @click="applyFilter">검색</el-button>
        <el-button @click="resetFilter">초기화</el-button>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div class="text-sm text-gray-600">
          <span v-if="selectedQuota">
            선택 직원: <b>{{ selectedQuota.employee_name }}</b> ({{ selectedQuota.leave_type_name }}, {{ selectedQuota.year }}년)
          </span>
          <span v-else>행을 클릭해 직원을 선택해 주세요.</span>
        </div>
        <div class="flex flex-wrap gap-2">
          <el-button type="primary" @click="openCreateQuotaDialog">+ 신규 할당</el-button>
          <el-button @click="openSelectedLeaveRequests" :disabled="!selectedQuota">신청내역</el-button>
          <el-button @click="openSelectedAdjustment" :disabled="!selectedQuota">조정</el-button>
          <el-button type="success" @click="exportCsv">엑셀 다운로드</el-button>
        </div>
      </div>

      <div class="quota-grid-wrap">
        <ag-grid-vue
          class="ag-theme-alpine"
          style="height: 100%; width: 100%"
          :columnDefs="quotaColumnDefs"
          :rowData="leaveQuotas"
          :defaultColDef="defaultColDef"
          :getRowClass="getLeaveRowClass"
          @rowClicked="onEmployeeSelect"
          @grid-ready="onGridReady"
        />
      </div>
    </el-card>
  </div>

  <el-dialog v-model="leaveDialogVisible" width="1000px" :title="`${selectedEmployee.employee_name}의 휴가 신청 내역`">
    <el-button type="success" @click="exportCsv2(selectedEmployee.employee_name)">엑셀 다운로드</el-button>

    <ag-grid-vue
      class="ag-theme-alpine"
      style="height: 400px; width: 100%"
      :columnDefs="leaveColumnDefs"
      :rowData="leaveRequests"
      :defaultColDef="defaultColDef"
      @rowClicked="onLeaveRowClick"
      @grid-ready="onGridReady2"
    />
  </el-dialog>

  <el-dialog v-model="stepDialogVisible" title="승인 단계별 상태" width="600px">
    <el-timeline>
      <el-timeline-item
        v-for="step in stepStatuses"
        :key="step.sequence"
        :timestamp="step.approved_date"
        :type="step.statusColor"
      >
        <div class="font-semibold">{{ step.approver_name }} ({{ step.statusLabel }})</div>
        <div v-if="step.comments" class="text-sm text-gray-500 mt-1">의견: {{ step.comments }}</div>
      </el-timeline-item>
    </el-timeline>
  </el-dialog>

  <el-dialog v-model="adjustDialogVisible" width="980px" :title="adjustmentTarget ? `${adjustmentTarget.employee_name} 휴가 조정` : '휴가 조정'">
    <div v-if="adjustmentTarget" class="mb-3 text-sm text-gray-600">
      <span>직원: {{ adjustmentTarget.employee_name }}</span>
      <span class="ml-3">유형: {{ adjustmentTarget.leave_type_name }}</span>
      <span class="ml-3">연도: {{ adjustmentTarget.year }}</span>
    </div>

    <div v-if="adjustmentTarget" class="summary-grid mb-3">
      <el-card shadow="never" class="summary-card">
        <div class="summary-label">기본일수</div>
        <div class="summary-value">{{ formatPlainNumber(adjustmentTarget.base_days) }}</div>
      </el-card>
      <el-card shadow="never" class="summary-card">
        <div class="summary-label">추가일수</div>
        <div class="summary-value">{{ formatPlainNumber(adjustmentTarget.extra_days) }}</div>
      </el-card>
      <el-card shadow="never" class="summary-card">
        <div class="summary-label">총일수</div>
        <div class="summary-value">{{ formatPlainNumber(adjustmentTarget.total_days) }}</div>
      </el-card>
      <el-card shadow="never" class="summary-card">
        <div class="summary-label">사용일수</div>
        <div class="summary-value">{{ formatPlainNumber(adjustmentTarget.used_days) }}</div>
      </el-card>
      <el-card shadow="never" class="summary-card">
        <div class="summary-label">현재 잔여</div>
        <div class="summary-value">{{ formatPlainNumber(adjustmentTarget.remaining_days) }}</div>
      </el-card>
    </div>

    <el-table :data="leaveAdjustments || []" size="small" style="width: 100%">
      <el-table-column prop="effective_date" label="근거일자" width="120" :formatter="formatDateCell" />
      <el-table-column label="조정항목" width="110">
        <template #default="{ row }">
          <el-tag :type="getAdjustmentBucketTagType(row)" size="small">
            {{ getAdjustmentBucketLabel(row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="반영일수" width="100">
        <template #default="{ row }">
          {{ formatSignedDays(row.days) }}
        </template>
      </el-table-column>
      <el-table-column prop="reason" label="사유" min-width="220" />
      <el-table-column prop="created_at" label="생성일시" width="160" :formatter="formatDateTimeCell" />
      <el-table-column prop="updated_at" label="수정일시" width="160" :formatter="formatDateTimeCell" />
      <el-table-column label="관리" width="150">
        <template #default="{ row }">
          <el-button size="small" @click="startEditAdjustment(row)">수정</el-button>
          <el-button size="small" type="danger" @click="voidAdjustment(row)">취소</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="mt-4 border-t pt-4 space-y-3">
      <div class="text-sm font-semibold">{{ adjustmentForm.id ? '조정 수정' : '조정 등록' }}</div>
      <el-form :model="adjustmentForm" label-width="100px" class="space-y-4">
        <el-form-item label="입사일">
          <el-input :model-value="adjustmentJoinDate" disabled />
        </el-form-item>

        <el-form-item label="휴가유형">
          <el-input :model-value="adjustmentTarget?.leave_type_name || ''" disabled />
        </el-form-item>

        <el-form-item label="연도">
          <el-input :model-value="adjustmentTarget?.year || ''" disabled />
        </el-form-item>

        <el-form-item label="기본일수" required>
          <el-input-number
            v-model="adjustmentForm.target_base_days"
            :min="0"
            :max="365"
            :step="0.5"
            :precision="1"
            :disabled="adjustmentForm.id && adjustmentEditBucket !== 'base'"
            style="width: 100%"
          />
          <div class="text-xs text-gray-500 mt-1">
            기준값 {{ formatPlainNumber(adjustmentBaseBaseline) }} / 반영 차이 {{ formatSignedDays(adjustmentBaseDelta) }}
          </div>
        </el-form-item>

        <el-form-item label="추가일수">
          <el-input-number
            v-model="adjustmentForm.target_extra_days"
            :min="0"
            :max="365"
            :step="0.5"
            :precision="1"
            :disabled="adjustmentForm.id && adjustmentEditBucket !== 'extra'"
            style="width: 100%"
          />
          <div class="text-xs text-gray-500 mt-1">
            기준값 {{ formatPlainNumber(adjustmentExtraBaseline) }} / 반영 차이 {{ formatSignedDays(adjustmentExtraDelta) }}
          </div>
        </el-form-item>

        <el-form-item label="총일수">
          <el-input-number
            :model-value="adjustmentTargetTotalDays"
            disabled
            :precision="1"
            style="width: 100%"
          />
          <div class="text-xs text-gray-500 mt-1">기본일수 + 추가일수 자동 계산</div>
        </el-form-item>

        <el-form-item label="근거일자" required>
          <el-date-picker
            v-model="adjustmentForm.effective_date"
            type="date"
            value-format="YYYY-MM-DD"
            placeholder="근거일자"
            style="width: 100%"
          />
          <div class="text-xs text-gray-500 mt-1">휴가 조정이 발생한 기준일자를 입력합니다.</div>
        </el-form-item>

        <el-form-item label="사유">
          <el-select v-model="adjustmentForm.reason_template" placeholder="사유 템플릿" clearable style="width: 220px">
            <el-option v-for="option in adjustmentReasonOptions" :key="option" :label="option" :value="option" />
          </el-select>
          <el-input
            v-model="adjustmentForm.reason"
            placeholder="조정 사유를 입력해 주세요."
            type="textarea"
            :rows="2"
            style="width: calc(100% - 232px)"
          />
        </el-form-item>

        <div v-if="adjustmentForm.id" class="text-xs text-gray-500">
          기존 조정 수정은 해당 조정 항목만 변경할 수 있습니다.
        </div>
      </el-form>

      <div class="text-sm text-gray-600">
        적용 후 예상 잔여:
        <b :class="adjustmentExpectedRemainingDays >= 0 ? 'text-blue-600' : 'text-red-600'">
          {{ formatPlainNumber(adjustmentExpectedRemainingDays) }}
        </b>
      </div>

      <div class="flex justify-end gap-2">
        <el-button v-if="adjustmentForm.id" @click="resetAdjustmentForm">취소</el-button>
        <el-button type="primary" @click="saveAdjustment">{{ adjustmentForm.id ? '수정 저장' : '조정 등록' }}</el-button>
      </div>
    </div>
  </el-dialog>

  <el-dialog v-model="createQuotaDialogVisible" width="500px" title="신규 휴가 할당">
    <el-form :model="createQuotaForm" label-width="100px" class="space-y-4">
      <el-form-item label="직원" required>
        <el-select
          v-model="createQuotaForm.employee"
          filterable
          remote
          :remote-method="searchEmployees"
          :loading="employeeLoading"
          placeholder="직원명 검색"
          style="width: 100%"
        >
          <el-option
            v-for="emp in employeeOptions"
            :key="emp.id"
            :label="`${emp.last_name}${emp.first_name} (${emp.department_name || '-'})`"
            :value="emp.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="입사일">
        <el-input
          :model-value="selectedCreateEmployeeJoinDate"
          disabled
          placeholder="직원을 선택하면 입사일이 표시됩니다."
        />
      </el-form-item>

      <el-form-item label="휴가유형" required>
        <el-select v-model="createQuotaForm.leave_type" placeholder="휴가유형 선택" style="width: 100%">
          <el-option
            v-for="lt in leaveTypeOptions"
            :key="lt.id"
            :label="lt.name"
            :value="lt.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="연도" required>
        <el-input-number
          v-model="createQuotaForm.year"
          :min="2020"
          :max="2100"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item label="기본일수" required>
        <el-input-number
          v-model="createQuotaForm.base_days"
          :min="0"
          :max="365"
          :step="0.5"
          :precision="1"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item label="추가일수">
        <el-input-number
          v-model="createQuotaForm.extra_days"
          :min="0"
          :max="365"
          :step="0.5"
          :precision="1"
          style="width: 100%"
        />
        <div class="text-xs text-gray-500 mt-1">근속 가산/보상 휴가 등을 반영합니다.</div>
      </el-form-item>

      <el-form-item label="총일수">
        <el-input-number
          v-model="computedTotalDays"
          disabled
          :precision="1"
          style="width: 100%"
        />
        <div class="text-xs text-gray-500 mt-1">기본일수 + 추가일수 자동 계산</div>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="createQuotaDialogVisible = false">취소</el-button>
      <el-button type="primary" @click="saveCreateQuota" :loading="createQuotaSaving">저장</el-button>
    </template>
  </el-dialog>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axiosInstance from '@/api/axios'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { AgGridVue } from 'ag-grid-vue3'
import { ElMessage, ElMessageBox } from 'element-plus'
import LeaveAdminSectionNav from '@/components/LeaveAdminSectionNav.vue'

const gridApi = ref(null)
const gridApi2 = ref(null)

const currentYear = new Date().getFullYear()

const leaveQuotas = ref([])
const leaveRequests = ref([])
const selectedEmployee = ref({ employee_name: '' })
const selectedQuota = ref(null)
const leaveDialogVisible = ref(false)
const adjustDialogVisible = ref(false)

// 신규 휴가 할당
const createQuotaDialogVisible = ref(false)
const createQuotaSaving = ref(false)
const employeeLoading = ref(false)
const employeeOptions = ref([])
const leaveTypeOptions = ref([])

const createQuotaForm = ref({
  employee: null,
  leave_type: null,
  year: currentYear,
  base_days: 15,
  extra_days: 0,
})

const formatYmd = (value) => {
  if (!value) return ''
  if (typeof value === 'string') {
    const ymd = value.match(/^\d{4}-\d{2}-\d{2}/)
    if (ymd) return ymd[0]
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const computedTotalDays = computed(() => {
  return (createQuotaForm.value.base_days || 0) + (createQuotaForm.value.extra_days || 0)
})

const selectedCreateEmployeeJoinDate = computed(() => {
  if (!createQuotaForm.value.employee) return ''
  const selectedEmployeeItem = employeeOptions.value.find(
    (emp) => String(emp.id) === String(createQuotaForm.value.employee),
  )
  return formatYmd(selectedEmployeeItem?.join_date ?? selectedEmployeeItem?.employee_join_date)
})

const openCreateQuotaDialog = async () => {
  createQuotaForm.value = {
    employee: null,
    leave_type: null,
    year: currentYear,
    base_days: 15,
    extra_days: 0,
  }
  employeeOptions.value = []
  await fetchLeaveTypes()
  createQuotaDialogVisible.value = true
}

const extractApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail
  const firstKey = Object.keys(data)[0]
  if (!firstKey) return fallback
  const value = data[firstKey]
  return Array.isArray(value) ? value[0] : value || fallback
}

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const fetchLeaveTypes = async () => {
  try {
    const res = await axiosInstance.get('leave-types/')
    leaveTypeOptions.value = normalizeListResponse(res.data)
    return leaveTypeOptions.value
  } catch (err) {
    console.error('휴가 유형 조회 실패', err)
    ElMessage.error(extractApiErrorMessage(err, '휴가 유형 목록 조회에 실패했습니다.'))
    return []
  }
}

const searchEmployees = async (query) => {
  if (!query || query.length < 1) {
    employeeOptions.value = []
    return
  }
  employeeLoading.value = true
  try {
    const res = await axiosInstance.get('employees/', { params: { search: query } })
    employeeOptions.value = Array.isArray(res.data) ? res.data : (res.data.results ?? [])
  } catch (err) {
    console.error('직원 검색 실패', err)
    employeeOptions.value = []
  } finally {
    employeeLoading.value = false
  }
}

const saveCreateQuota = async () => {
  if (!createQuotaForm.value.employee) {
    ElMessage.warning('직원을 선택해 주세요.')
    return
  }
  if (!createQuotaForm.value.leave_type) {
    ElMessage.warning('휴가 유형을 선택해 주세요.')
    return
  }
  if (!createQuotaForm.value.year) {
    ElMessage.warning('연도를 입력해 주세요.')
    return
  }
  if (createQuotaForm.value.base_days === null || createQuotaForm.value.base_days < 0) {
    ElMessage.warning('기본 일수를 입력해 주세요.')
    return
  }

  createQuotaSaving.value = true
  try {
    const payload = {
      employee: createQuotaForm.value.employee,
      leave_type: createQuotaForm.value.leave_type,
      year: createQuotaForm.value.year,
      base_days: createQuotaForm.value.base_days,
      extra_days: createQuotaForm.value.extra_days || 0,
      total_days: computedTotalDays.value,
      remaining_days: computedTotalDays.value,
    }

    await axiosInstance.post('leave-quota/', payload)
    ElMessage.success('휴가 할당을 생성했습니다.')
    createQuotaDialogVisible.value = false
    await refreshLeaveQuotaList()
  } catch (err) {
    console.error('휴가 할당 생성 실패', err)
    ElMessage.error(extractApiErrorMessage(err, '휴가 할당 생성에 실패했습니다.'))
  } finally {
    createQuotaSaving.value = false
  }
}

const resolveRowId = (row) => {
  if (!row) return null
  return row.id ?? row.pk ?? row.leave_quota_id ?? row.quota_id ?? null
}

const selectedQuotaId = computed(() => resolveRowId(selectedQuota.value))

const adjustmentTarget = ref(null)
const leaveAdjustments = ref([])
const adjustmentReasonOptions = [
  '근속 추가 일수 반영',
  '오지급 정정',
  '입/퇴사 정산',
  '기타',
]
const adjustmentForm = ref({
  id: null,
  effective_date: '',
  target_base_days: 0,
  target_extra_days: 0,
  edit_bucket: '',
  original_days: 0,
  reason_template: '',
  reason: '',
})

const toNumber = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const formatPlainNumber = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0'
  return Number.isInteger(n) ? `${n}` : n.toFixed(1)
}

const formatSignedDays = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return '0'
  const abs = Math.abs(n)
  const formatted = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1)
  return `${n > 0 ? '+' : '-'}${formatted}`
}

const inferAdjustmentBucket = (row) => {
  const bucket = row?.allocation_bucket
  if (bucket === 'base' || bucket === 'extra' || bucket === 'carry_forward') {
    return bucket === 'carry_forward' ? 'extra' : bucket
  }
  const reason = String(row?.reason ?? '')
  if (reason.startsWith('tenure_extra:') || reason.startsWith('carry_forward:')) {
    return 'extra'
  }
  return 'base'
}

const getAdjustmentBucketLabel = (row) => {
  return inferAdjustmentBucket(row) === 'extra' ? '추가일수' : '기본일수'
}

const getAdjustmentBucketTagType = (row) => {
  return inferAdjustmentBucket(row) === 'extra' ? 'success' : 'info'
}

const adjustmentEditBucket = computed(() => inferAdjustmentBucket({ allocation_bucket: adjustmentForm.value.edit_bucket }))

const adjustmentBaseBaseline = computed(() => {
  const current = toNumber(adjustmentTarget.value?.base_days)
  if (adjustmentForm.value.id && adjustmentEditBucket.value === 'base') {
    return current - toNumber(adjustmentForm.value.original_days)
  }
  return current
})

const adjustmentExtraBaseline = computed(() => {
  const current = toNumber(adjustmentTarget.value?.extra_days)
  if (adjustmentForm.value.id && adjustmentEditBucket.value === 'extra') {
    return current - toNumber(adjustmentForm.value.original_days)
  }
  return current
})

const adjustmentBaseDelta = computed(() => {
  return toNumber(adjustmentForm.value.target_base_days) - adjustmentBaseBaseline.value
})

const adjustmentExtraDelta = computed(() => {
  return toNumber(adjustmentForm.value.target_extra_days) - adjustmentExtraBaseline.value
})

const adjustmentTargetTotalDays = computed(() => {
  return toNumber(adjustmentForm.value.target_base_days) + toNumber(adjustmentForm.value.target_extra_days)
})

const adjustmentExpectedRemainingDays = computed(() => {
  const currentRemaining = toNumber(adjustmentTarget.value?.remaining_days)
  return currentRemaining + adjustmentBaseDelta.value + adjustmentExtraDelta.value
})

const adjustmentJoinDate = computed(() => formatYmd(adjustmentTarget.value?.employee_join_date))

watch(
  () => adjustmentForm.value.reason_template,
  (template) => {
    if (!template) return
    if (!adjustmentForm.value.reason?.trim()) {
      adjustmentForm.value.reason = template
    }
  },
)

const fmtDate = (p) => {
  return formatYmd(p.value)
}

const fmtNum = (p) => {
  const v = p.value
  if (v === null || v === undefined || v === '') return ''
  const n = Number(v)
  return Number.isNaN(n) ? v : n.toLocaleString()
}

const fmtApproval = (p) => ({ pending: '대기', approved: '승인', rejected: '반려' }[p.value] ?? p.value)
const fmtSession = (p) => ({ morning: '오전', afternoon: '오후', full: '하루' }[p.value] ?? p.value)

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
  suppressMenuHide: true,
}

function onGridReady(params) {
  gridApi.value = params.api
}

function onGridReady2(params) {
  gridApi2.value = params.api
}

const quotaColumnDefs = [
  { headerName: '상태', field: 'pending_status', pinned: 'left', minWidth: 90, maxWidth: 110, flex: 0.6 },
  { headerName: '직원명', field: 'employee_name', pinned: 'left', minWidth: 120, maxWidth: 160, flex: 0.9 },
  { headerName: '입사일', field: 'employee_join_date', valueFormatter: fmtDate, minWidth: 110, maxWidth: 140, flex: 0.8 },
  { headerName: '부서', field: 'department_name', minWidth: 110, maxWidth: 150, flex: 0.8, hide: true },
  { headerName: '회사명', field: 'company_name', minWidth: 120, maxWidth: 160, flex: 0.9, hide: true },
  { headerName: '연도', field: 'year', minWidth: 90, maxWidth: 110, flex: 0.6 },
  { headerName: '휴가유형', field: 'leave_type_name', minWidth: 110, maxWidth: 150, flex: 0.8 },
  { headerName: '기본일수', field: 'base_days', valueFormatter: fmtNum, cellClass: 'ag-right-aligned-cell', minWidth: 90, maxWidth: 120, flex: 0.6 },
  { headerName: '추가일수', field: 'extra_days', valueFormatter: fmtNum, cellClass: 'ag-right-aligned-cell', minWidth: 90, maxWidth: 120, flex: 0.6 },
  { headerName: '총일수', field: 'total_days', valueFormatter: fmtNum, cellClass: 'ag-right-aligned-cell', minWidth: 100, maxWidth: 130, flex: 0.7 },
  { headerName: '사용일수', field: 'used_days', valueFormatter: fmtNum, cellClass: 'ag-right-aligned-cell', minWidth: 100, maxWidth: 130, flex: 0.7 },
  { headerName: '잔여일수', field: 'remaining_days', valueFormatter: fmtNum, cellClass: 'ag-right-aligned-cell', minWidth: 100, maxWidth: 130, flex: 0.7 },
  { headerName: '업데이트일자', field: 'calculation_date', valueFormatter: fmtDate, minWidth: 120, maxWidth: 150, flex: 0.8, hide: true },
]

// 증빙 첨부 셀 — ag-grid cellRenderer 는 HTML 문자열을 그대로 innerHTML 로 넣는다.
// 파일명은 사용자 업로드 값이라 반드시 이스케이프해야 XSS 가 생기지 않는다.
const escapeHtml = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]))

const attachmentCellRenderer = (params) => {
  const atts = params?.data?.attachments || []
  if (!atts.length) return '<span style="color:#c0c4cc;">-</span>'
  return atts
    .map((a) => {
      const raw = a.file_url || a.file || ''
      // 우리 서버가 준 경로만 링크로 만든다(javascript: 등 차단)
      const safeUrl = /^(https?:\/\/|\/)/.test(raw) ? escapeHtml(raw) : ''
      const name = escapeHtml(a.original_name || '첨부파일')
      if (!safeUrl) return `<span>${name}</span>`
      return `<a href="${safeUrl}" target="_blank" rel="noopener" style="color:#409eff;text-decoration:none;">📎 ${name}</a>`
    })
    .join('&nbsp;&nbsp;')
}

const leaveColumnDefs = [
  { headerName: '승인상태', field: 'approval_status', valueFormatter: fmtApproval, pinned: 'left', minWidth: 100, maxWidth: 130, flex: 0.7 },
  { headerName: '신청일', field: 'created_at', valueFormatter: fmtDate, pinned: 'left', minWidth: 120, maxWidth: 150, flex: 0.8 },
  { headerName: '휴가일수', field: 'days_requested', valueFormatter: fmtNum, cellClass: 'ag-right-aligned-cell', minWidth: 100, maxWidth: 130, flex: 0.7 },
  { headerName: '시작일', field: 'start_date', valueFormatter: fmtDate, minWidth: 120, maxWidth: 150, flex: 0.8 },
  { headerName: '종료일', field: 'end_date', valueFormatter: fmtDate, minWidth: 120, maxWidth: 150, flex: 0.8 },
  { headerName: '휴가유형', field: 'leave_type_name', minWidth: 110, maxWidth: 150, flex: 0.8 },
  { headerName: '구분', field: 'session', valueFormatter: fmtSession, minWidth: 90, maxWidth: 120, flex: 0.6 },
  { headerName: '증빙', field: 'attachments', cellRenderer: attachmentCellRenderer, minWidth: 180, flex: 1.1, sortable: false, filter: false },
  { headerName: '사유', field: 'reason', minWidth: 160, flex: 1.1, hide: true },
  { headerName: '반송사유', field: 'rejection_reason', minWidth: 160, flex: 1.1, hide: true },
  { headerName: '승인자', field: 'approved_by', minWidth: 110, maxWidth: 150, flex: 0.8, hide: true },
  { headerName: '승인일', field: 'approved_at', valueFormatter: fmtDate, minWidth: 120, maxWidth: 150, flex: 0.8, hide: true },
]

const formatSession = (value) => {
  switch (value) {
    case 'morning':
      return '오전'
    case 'afternoon':
      return '오후'
    case 'full':
      return '전일'
    default:
      return value
  }
}

const formatApprovalStatus = (status) => {
  switch (status) {
    case 'pending':
      return '대기'
    case 'approved':
      return '승인'
    case 'rejected':
      return '반려'
    case 'none':
      return ''
    default:
      return ''
  }
}

const getLeaveRowClass = (params) => {
  const classes = []
  if (params.data && params.data.pending_status === '대기') {
    classes.push('highlight-pending-row')
  }
  if (resolveRowId(params.data) && resolveRowId(params.data) === selectedQuotaId.value) {
    classes.push('selected-row')
  }
  return classes.join(' ')
}

const getDefaultEffectiveDate = () => {
  const targetYear = adjustmentTarget.value?.year ?? currentYear
  return `${targetYear}-01-01`
}

const buildAdjustmentTarget = (row) => ({
  employee: row.employee ?? row.employee_id ?? null,
  employee_name: row.employee_name,
  employee_join_date: row.employee_join_date ?? row.join_date ?? null,
  leave_type: row.leave_type ?? row.leave_type_id ?? null,
  leave_type_name: row.leave_type_name,
  year: row.year,
  base_days: toNumber(row.base_days),
  extra_days: toNumber(row.extra_days),
  total_days: toNumber(row.total_days),
  used_days: toNumber(row.used_days),
  remaining_days: toNumber(row.remaining_days),
})

const resetAdjustmentForm = () => {
  adjustmentForm.value = {
    id: null,
    effective_date: getDefaultEffectiveDate(),
    target_base_days: toNumber(adjustmentTarget.value?.base_days),
    target_extra_days: toNumber(adjustmentTarget.value?.extra_days),
    edit_bucket: '',
    original_days: 0,
    reason_template: '',
    reason: '',
  }
}

const isQuotaOpeningAdjustment = (row) => {
  const reason = String(row?.reason ?? '')
  return row?.entry_kind === 'opening' || reason.startsWith('tenure_extra:') || reason.startsWith('carry_forward:')
}

const fetchLeaveAdjustments = async () => {
  if (!adjustmentTarget.value) return
  const params = {
    employee_id: adjustmentTarget.value.employee,
    leave_type_id: adjustmentTarget.value.leave_type,
    year: adjustmentTarget.value.year,
    accrual_type: 'manual_adjust',
    is_void: false,
  }
  const res = await axiosInstance.get('leave-accruals/', { params })
  const list = Array.isArray(res.data) ? res.data : (res.data.results ?? [])
  leaveAdjustments.value = list.filter((row) => !isQuotaOpeningAdjustment(row))
}

const openAdjustmentDialog = async (row) => {
  adjustmentTarget.value = buildAdjustmentTarget(row)
  resetAdjustmentForm()
  adjustDialogVisible.value = true
  await fetchLeaveAdjustments()
}

const startEditAdjustment = (row) => {
  const bucket = inferAdjustmentBucket(row)
  const safeDays = toNumber(row.days)
  adjustmentForm.value = {
    id: row.id,
    effective_date: row.effective_date,
    target_base_days: bucket === 'base'
      ? toNumber(adjustmentTarget.value?.base_days)
      : adjustmentBaseBaseline.value,
    target_extra_days: bucket === 'extra'
      ? toNumber(adjustmentTarget.value?.extra_days)
      : adjustmentExtraBaseline.value,
    edit_bucket: bucket,
    original_days: safeDays,
    reason_template: '',
    reason: row.reason ?? '',
  }
}

const saveAdjustment = async () => {
  if (!adjustmentTarget.value) return
  if (!adjustmentForm.value.effective_date || !adjustmentForm.value.reason?.trim()) {
    ElMessage.warning('근거일자와 사유를 입력해 주세요.')
    return
  }

  try {
    if (adjustmentForm.value.id) {
      const bucket = adjustmentEditBucket.value
      const days = bucket === 'extra' ? adjustmentExtraDelta.value : adjustmentBaseDelta.value
      if (!Number.isFinite(days) || days === 0) {
        ElMessage.warning('변경된 조정 일수가 없습니다.')
        return
      }
      await axiosInstance.patch(`leave-accruals/${adjustmentForm.value.id}/`, {
        effective_date: adjustmentForm.value.effective_date,
        days,
        allocation_bucket: bucket,
        entry_kind: 'manual_adjust',
        reason: adjustmentForm.value.reason,
      })
    } else {
      const effectiveDate = new Date(adjustmentForm.value.effective_date)
      const month = Number.isNaN(effectiveDate.getTime()) ? null : effectiveDate.getMonth() + 1
      const requests = []
      if (adjustmentBaseDelta.value !== 0) {
        requests.push(
          axiosInstance.post('leave-accruals/', {
            effective_date: adjustmentForm.value.effective_date,
            days: adjustmentBaseDelta.value,
            reason: adjustmentForm.value.reason,
            employee: adjustmentTarget.value.employee,
            leave_type: adjustmentTarget.value.leave_type,
            year: adjustmentTarget.value.year,
            month,
            accrual_type: 'manual_adjust',
            allocation_bucket: 'base',
            entry_kind: 'manual_adjust',
          }),
        )
      }
      if (adjustmentExtraDelta.value !== 0) {
        requests.push(
          axiosInstance.post('leave-accruals/', {
            effective_date: adjustmentForm.value.effective_date,
            days: adjustmentExtraDelta.value,
            reason: adjustmentForm.value.reason,
            employee: adjustmentTarget.value.employee,
            leave_type: adjustmentTarget.value.leave_type,
            year: adjustmentTarget.value.year,
            month,
            accrual_type: 'manual_adjust',
            allocation_bucket: 'extra',
            entry_kind: 'manual_adjust',
          }),
        )
      }
      if (!requests.length) {
        ElMessage.warning('변경된 기본일수 또는 추가일수가 없습니다.')
        return
      }
      await Promise.all(requests)
    }

    await fetchLeaveAdjustments()
    await refreshLeaveQuotaList()
    if (selectedQuota.value) {
      adjustmentTarget.value = buildAdjustmentTarget(selectedQuota.value)
    }
    resetAdjustmentForm()
    ElMessage.success('저장되었습니다.')
  } catch (err) {
    console.error('조정 저장 실패', err)
    ElMessage.error('조정 저장에 실패했습니다.')
  }
}

const voidAdjustment = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt('취소 사유를 입력해 주세요.', '조정 취소', {
      confirmButtonText: '취소',
      cancelButtonText: '닫기',
      inputType: 'textarea',
      inputValidator: (val) => !!val?.trim(),
      inputErrorMessage: '취소 사유는 필수입니다.',
    })

    await axiosInstance.post(`leave-accruals/${row.id}/void/`, { reason: value })
    await fetchLeaveAdjustments()
    await refreshLeaveQuotaList()
    ElMessage.success('조정을 취소했습니다.')
  } catch (err) {
    if (err === 'cancel' || err === 'close') return
    console.error('조정 취소 실패', err)
    ElMessage.error('조정 취소에 실패했습니다.')
  }
}

const fetchLeaveRequestsForRow = async (row) => {
  if (!row) return
  try {
    const yearParam = normalizeYear(filters.value.year) ?? currentYear
    const res = await axiosInstance.get('leaves/', {
      params: {
        employee_id: row.employee,
        year: yearParam,
      },
    })

    leaveRequests.value = res.data.results.map((item) => ({
      ...item,
      created_at: formatDate(item.created_at),
      approved_at: formatDate(item.approved_at),
      session: formatSession(item.session),
      approval_status: formatApprovalStatus(item.approval_status),
    }))
  } catch (err) {
    console.error('휴가 신청내역 조회 실패', err)
    ElMessage.error(extractApiErrorMessage(err, '휴가 신청내역 조회에 실패했습니다.'))
    leaveRequests.value = []
  }
}

const onEmployeeSelect = (event) => {
  selectedQuota.value = event?.data ?? null
}

const openSelectedLeaveRequests = async () => {
  if (!selectedQuota.value) {
    ElMessage.warning('직원을 먼저 선택해 주세요.')
    return
  }
  selectedEmployee.value = selectedQuota.value
  await fetchLeaveRequestsForRow(selectedQuota.value)
  leaveDialogVisible.value = true
}

const openSelectedAdjustment = async () => {
  if (!selectedQuota.value) {
    ElMessage.warning('조정할 직원을 선택해 주세요.')
    return
  }
  await openAdjustmentDialog(selectedQuota.value)
}

// 승인 단계 조회
const stepDialogVisible = ref(false)
const stepStatuses = ref([])

const getStepStatusLabel = (statusCode) => {
  switch (statusCode) {
    case 'pending':
      return '대기'
    case 'approved':
      return '승인'
    case 'rejected':
      return '반려'
    default:
      return '알 수 없음'
  }
}

const getStatusColor = (statusCode) => {
  switch (statusCode) {
    case 'approved':
      return 'success'
    case 'rejected':
      return 'danger'
    case 'pending':
      return 'info'
    default:
      return 'gray'
  }
}

const onLeaveRowClick = async (event) => {
  const leaveId = event.data.id
  try {
    const { data } = await axiosInstance.get(`/leaves/${leaveId}/approval-steps/`)

    stepStatuses.value = data.map((step) => ({
      sequence: step.sequence,
      approver_name: step.approver_name,
      approved_date: formatDate(step.approved_date),
      comments: step.comments,
      statusLabel: getStepStatusLabel(step.status),
      statusColor: getStatusColor(step.status),
    }))
    stepDialogVisible.value = true
  } catch (err) {
    console.error('승인 단계 조회 실패', err)
  }
}

// 필터
const originalLeaveQuotas = ref([])
const summaryCards = computed(() => {
  const rows = originalLeaveQuotas.value || []
  const pendingCount = rows.filter((row) => row.pending_status === '대기').length
  const exhaustedCount = rows.filter((row) => Number(row.remaining_days || 0) <= 0).length
  const lowBalanceCount = rows.filter((row) => Number(row.remaining_days || 0) > 0 && Number(row.remaining_days || 0) <= 3).length

  return [
    { key: 'total', label: '조회 대상', value: `${rows.length}건` },
    { key: 'pending', label: '승인 대기', value: `${pendingCount}건` },
    { key: 'exhausted', label: '잔여 0 이하', value: `${exhaustedCount}건` },
    { key: 'low', label: '잔여 3일 이하', value: `${lowBalanceCount}건` },
  ]
})

const filters = ref({
  employee_name: '',
  company_name: '',
  department_name: '',
  pending_status: '',
  year: currentYear,
})

const companyOptions = ref([])
const departmentOptions = ref([])
const lastFetchedYear = ref(null)

const normalizeYear = (value) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return null
  const yearNum = Number(trimmed)
  return Number.isNaN(yearNum) ? null : yearNum
}

const fetchLeaveQuotas = async (yearValue) => {
  const yearParam = normalizeYear(yearValue)
  const params = yearParam ? { year: yearParam } : {}

  // 1) 일반 LeaveQuota
  const res = await axiosInstance.get('leave-quota/', { params })
  const results = res.data.results.map((item) => ({
    ...item,
    id: item.id ?? item.pk ?? item.leave_quota_id ?? null,
    employee: item.employee ?? item.employee_id ?? null,
    leave_type: item.leave_type ?? item.leave_type_id ?? null,
    employee_join_date: item.employee_join_date ?? item.join_date ?? null,
    pending_status: item.pending_status === 'none' ? '' : formatApprovalStatus(item.pending_status),
    is_birthday_leave: false,
  }))

  // 2) 생일반차 Grant — 별도 트랙이지만 통합 표시
  try {
    const grantRes = await axiosInstance.get('birthday-grants/', { params })
    const grantResults = (grantRes.data.results || grantRes.data || []).map((g) => ({
      id: `birthday-${g.id}`,
      employee_id: g.employee_id,
      employee_name: g.employee_name,
      leave_type_name: '생일반차',
      year: g.grant_year,
      base_days: g.days,
      extra_days: 0,
      carry_days: 0,
      total_days: g.days,
      used_days: g.status === 'USED' ? g.days : 0,
      remaining_days: (g.status === 'GRANTED' || g.status === 'REQUESTED') ? g.days : 0,
      exceeded_days: 0,
      valid_from: g.valid_from,
      valid_to: g.valid_to,
      birthday_status: g.status,
      pending_status: g.status === 'REQUESTED' ? '결재 대기' : '',
      is_birthday_leave: true,
    }))
    results.push(...grantResults)
  } catch (err) {
    console.warn('birthday-grants 조회 실패 (권한 또는 API 미가용):', err?.response?.status)
  }

  leaveQuotas.value = results
  originalLeaveQuotas.value = results
  lastFetchedYear.value = yearParam ?? currentYear
  if (selectedQuotaId.value) {
    selectedQuota.value = results.find((row) => resolveRowId(row) === selectedQuotaId.value) ?? null
  }

  companyOptions.value = [...new Set(results.map((item) => item.company_name).filter(Boolean))]
  departmentOptions.value = [...new Set(results.map((item) => item.department_name).filter(Boolean))]
}

const applyFilter = async () => {
  const yearParam = normalizeYear(filters.value.year)
  if ((yearParam ?? currentYear) !== lastFetchedYear.value) {
    await fetchLeaveQuotas(yearParam ?? currentYear)
  }
  const filtered = originalLeaveQuotas.value.filter((item) => {
    const matchEmployee = !filters.value.employee_name || item.employee_name?.includes(filters.value.employee_name)
    const matchCompany = !filters.value.company_name || item.company_name === filters.value.company_name
    const matchDepartment = !filters.value.department_name || item.department_name === filters.value.department_name
    const matchStatus = !filters.value.pending_status || item.pending_status === filters.value.pending_status
    return matchEmployee && matchCompany && matchDepartment && matchStatus
  })
  leaveQuotas.value = filtered
  if (selectedQuotaId.value) {
    selectedQuota.value = filtered.find((row) => resolveRowId(row) === selectedQuotaId.value) ?? null
  }
}

const refreshLeaveQuotaList = async () => {
  await fetchLeaveQuotas(lastFetchedYear.value ?? currentYear)
  await applyFilter()
}

const resetFilter = async () => {
  filters.value = {
    employee_name: '',
    company_name: '',
    department_name: '',
    pending_status: '',
    year: currentYear,
  }
  await fetchLeaveQuotas(currentYear)
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return date.toLocaleString('ko-KR')
}

const formatDateCell = (row, column, value) => formatDate(value)
const formatDateTimeCell = (row, column, value) => formatDateTime(value)

watch(leaveDialogVisible, (visible) => {
  if (!visible) {
    leaveRequests.value = []
  }
})

watch(adjustDialogVisible, (visible) => {
  if (!visible) {
    leaveAdjustments.value = []
    adjustmentTarget.value = null
    resetAdjustmentForm()
  }
})

onMounted(async () => {
  const types = await fetchLeaveTypes()
  if (!createQuotaForm.value.leave_type) {
    createQuotaForm.value.leave_type = types[0]?.id ?? null
  }
  await fetchLeaveQuotas(currentYear)
})

function exportCsv() {
  gridApi.value.exportDataAsCsv({
    fileName: `휴가관리_${new Date().toISOString().slice(0, 10)}.csv`,
  })
}

function exportCsv2(employeeName) {
  gridApi2.value.exportDataAsCsv({
    fileName: `${employeeName}_휴가신청내역_${new Date().toISOString().slice(0, 10)}.csv`,
  })
}
</script>

<style>
/* ⚠ 이 블록은 전역(non-scoped) — ag-grid row class 적용을 위해 필요하지만,
   화면 한정 규칙은 반드시 .leave-admin-view 네임스페이스 하위로 작성할 것.
   과거 `.el-card { padding:20px; border-radius:1rem }` 와 `.text-xl` 이 전역으로 새서
   이 화면 방문 후 전 화면의 카드가 부풀던 원인이었음 — 제거 (2026-06-11).
   (.text-xl 은 UiCard.vue 자체 스타일로 이전) */
.ag-theme-alpine {
  --ag-border-radius: 6px;
  --ag-header-background-color: #f9fafb;
}

/* summary-* 는 다이얼로그(teleport)와 타 화면에서도 쓰는 공용 패턴 — 전역 유지, 밀도만 압축 */
.summary-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-card {
  border: 1px solid #e5e7eb;
  flex: 0 0 auto;
  min-width: 120px;
}

.summary-card .el-card__body {
  padding: 8px 12px;
}

.summary-label {
  color: #6b7280;
  font-size: 12px;
}

.summary-value {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  margin-top: 2px;
}

.ag-theme-alpine .highlight-pending-row {
  background-color: #fff8e1 !important;
  font-weight: bold;
}

.ag-theme-alpine .selected-row {
  background-color: #eff6ff !important;
}

/* ===== 이중 스크롤 방지 (PC) =====
   그리드 고정 600px 이 상단 요소와 어긋나 페이지+그리드 이중 스크롤 유발
   → .content 가시 영역을 flex 로 채워 그리드 스크롤만 남김 (지출결의서 리스트와 동일 패턴) */
@media (min-width: 769px) {
  .leave-admin-view {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .leave-admin-view > :not(.quota-card) {
    flex-shrink: 0;
  }
  .leave-admin-view .quota-card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .leave-admin-view .quota-card > .el-card__body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .leave-admin-view .quota-card > .el-card__body > :not(.quota-grid-wrap) {
    flex-shrink: 0;
  }
  .leave-admin-view .quota-grid-wrap {
    flex: 1;
    min-height: 0;
  }
}

/* 모바일 폴백: flex 미적용이므로 그리드 높이 직접 지정 */
@media (max-width: 768px) {
  .leave-admin-view .quota-grid-wrap {
    height: 480px;
  }
}
</style>

